import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CubeIcon,
  UserCircleIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { NavLink, Outlet, useLocation } from 'react-router';
import logo from '@/assets/logo-white.svg';
import { useAuth } from '@/contexts/auth';

const navItems = [
  { to: '/orders', label: '発注', icon: ClipboardDocumentListIcon },
  { to: '/products', label: '商品', icon: CubeIcon },
];

const mobileTabItems = [...navItems, { to: '/account', label: 'アカウント', icon: UserCircleIcon }];

/** スマホで画面上部のロゴヘッダーと下部タブバーを出す画面(下層画面は各画面の戻るヘッダーを使う) */
const MOBILE_TOP_LEVEL_PATHS = mobileTabItems.map((item) => item.to);

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'flex flex-col items-center w-14 py-[3px] text-[15px] text-white duration-300',
    isActive ? 'bg-white/20 opacity-100' : 'opacity-50 hover:bg-white/20 hover:opacity-100',
  );

const mobileTabClasses = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'flex flex-col items-center justify-center gap-0.5 text-sm',
    isActive ? 'font-bold text-sky-800' : 'text-neutral-600',
  );

const PartnerSelect = ({ className }: { className?: string }) => {
  const { partners, selectedPartnerId, setSelectedPartnerId } = useAuth();
  return (
    <div className={clsx('flex min-w-0 items-center gap-2 text-white', className)}>
      <BuildingOffice2Icon className="size-5 shrink-0" />
      <select
        value={selectedPartnerId ?? ''}
        onChange={(e) => setSelectedPartnerId(Number(e.target.value))}
        aria-label="発注先"
        className="min-w-0 max-w-56 cursor-pointer truncate border border-white/30 bg-white/10 px-2 py-1 text-sm text-white outline-none"
      >
        {partners.map((partner) => (
          <option key={partner.id} value={partner.id} className="text-neutral-800">
            {partner.supplier_name}
          </option>
        ))}
      </select>
    </div>
  );
};

export const AppLayout = () => {
  const { profile, logout } = useAuth();
  const { pathname } = useLocation();
  const isMobileTopLevel = MOBILE_TOP_LEVEL_PATHS.includes(pathname);

  return (
    <div className={clsx('flex min-h-screen flex-col', isMobileTopLevel && 'pb-16 md:pb-0')}>
      <header
        className={clsx(
          'h-14 items-center justify-between gap-3 bg-[#00598a] px-4 md:flex md:px-5',
          isMobileTopLevel ? 'flex' : 'hidden',
        )}
      >
        <div className="flex items-center gap-5">
          <img src={logo} alt="Animal EDI" className="h-5" />
          <nav className="hidden items-center gap-4 md:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClasses}>
                <item.icon className="w-6" />
                <span className="text-[15px]">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex min-w-0 items-center gap-4">
          <PartnerSelect />
          <Menu as="div" className="relative hidden md:block">
            <MenuButton className="flex w-14 cursor-pointer flex-col items-center py-[3px] text-[15px] text-white opacity-50 duration-300 hover:bg-white/20 hover:opacity-100">
              <Cog6ToothIcon className="w-6" />
              <span className="text-[15px]">設定</span>
            </MenuButton>
            <MenuItems className="absolute right-0 z-50 mt-1 w-48 bg-white py-1 shadow-lg ring-1 ring-black/5">
              <div className="border-b border-neutral-200 px-4 py-2 text-sm text-neutral-600">
                {profile?.username}
              </div>
              <MenuItem>
                <button
                  type="button"
                  onClick={logout}
                  className="w-full px-4 py-2 text-left text-sm text-neutral-700 data-[focus]:bg-neutral-100"
                >
                  ログアウト
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </header>
      <main className="min-w-0 flex-1 overflow-x-clip">
        <Outlet />
      </main>
      {isMobileTopLevel && (
        <nav
          aria-label="メインメニュー"
          className="fixed inset-x-0 bottom-0 z-30 grid h-16 grid-cols-3 border-t border-neutral-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          {mobileTabItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={mobileTabClasses}>
              <item.icon className="size-6" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
};
